import React, { useState, useEffect } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import StartupsCard from "../components/startupCard";
import SectorFilterDialog from "../components/sectorFilterDialog";
import {
  Button,
  Pagination,
  MenuItem,
  Select,
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  FormControlLabel,
  Slider,
  Box
} from "@mui/material";

import SkeletonCard from "../components/skeleton/skeletonCard";
import YearRangeFilter from "../components/yearRangePicker";
import { useDebounce } from 'use-debounce';

const FILTER_STORAGE_KEY = "startups-filters";

const saveFiltersToSession = (filters, nameSearch, dialogPlaceholder) => {
  const payload = { filters, nameSearch, dialogPlaceholder };
  sessionStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(payload));
};

const getFiltersFromSession = () => {
  const stored = sessionStorage.getItem(FILTER_STORAGE_KEY);
  return stored ? JSON.parse(stored) : null;
};

const clearFiltersFromSession = () => {
  sessionStorage.removeItem(FILTER_STORAGE_KEY);
};

function Startups() {
  const [startupsData, setStartupsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [perPage] = useState(9);
  const [orderDir, setOrderDir] = useState("desc");
  const [orderBy] = useState("added");
  const [totalResults, setTotalResults] = useState(0);
  const [filters, setFilters] = useState({});
  const [tempYearRange, setTempYearRange] = useState([null, null]);
    
  const [nameSearch, setNameSearch] = useState("");
  const [debouncedNameSearch] = useDebounce(nameSearch, 500);


  // Tutti i filtri li gestiamo come stringhe
  const [selectedFilters, setSelectedFilters] = useState({
    chat: "",
    business_models: [],
    stages: [],
    company_types: [],
    target_markets: [],
    tags: [],
    trade_shows: [],
    order_by: "",
    order_dir: "",
    countries: [],
    year_min: null,
    year_max: null,
    categories: []
  });

  const location = useLocation();
  const navigate = useNavigate();

  const [resetSignal, setResetSignal] = useState(false);
  const [dialogPlaceholder, setDialogPlaceholder] = useState("");
  const [componentKey, setComponentKey] = useState(0);

  // Nuovo stato per il client (se presente)
  const [clientName, setClientName] = useState("");

  const [availableYears, setAvailableYears] = useState([]);

  const [isInitialLoad, setIsInitialLoad] = useState(true);
  
  // Funzione per convertire i parametri della query string in un oggetto
  const parseQueryParams = (search) => {
    const params = new URLSearchParams(search);
    const parsedParams = {};
    for (const [key, value] of params.entries()) {
      parsedParams[key] = value;
    }
    return parsedParams;
  };

  // Funzione per ottenere i dati dei filtri
  const fetchFilterData = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/values/innovation-filters`
      );
      
      const { year_min, year_max, tags, years } = response.data.data;
  
      setFilters({
        ...response.data.data,
        countries: response.data.data.countries?.sort((a, b) => a.name.localeCompare(b.name)),
        tags: tags?.filter(tag => tag.type === "standard"),
        trade_shows: tags?.filter(tag => tag.type === "exhibition")
      });
  
      if (years && Array.isArray(years)) {
        setAvailableYears(years);
      }     
  
    } catch (err) {
      console.error("Errore nel caricamento dei filtri", err);
    }
  };

  // Funzione per ottenere i dati delle startup
  const fetchData = async (page) => {
  setLoading(true);
  setError(null);

  try {
    const token = localStorage.getItem("token");
    if (!token) throw new Error("Token non trovato. Effettua il login.");

    const queryParams = new URLSearchParams();
    queryParams.append("page", page);
    queryParams.append("per_page", perPage);
    queryParams.append("order_dir", orderDir);
    queryParams.append("order_by", orderBy);

    // Aggiungi i filtri da selectedFilters
    if (selectedFilters.chat) queryParams.append("chat", selectedFilters.chat);
    selectedFilters.countries.forEach(id => queryParams.append("country", id));
    if (selectedFilters.year_min !== null) queryParams.append("year_min", selectedFilters.year_min);
    if (selectedFilters.year_max !== null) queryParams.append("year_max", selectedFilters.year_max);
    selectedFilters.business_models.forEach(id => queryParams.append("business_model", id));
    selectedFilters.stages.forEach(id => queryParams.append("status", id));
    selectedFilters.company_types.forEach(id => queryParams.append("type", id));
    selectedFilters.target_markets.forEach(id => queryParams.append("target_markets[]", id));
    selectedFilters.tags.forEach(id => queryParams.append("tags[]", id));
    selectedFilters.trade_shows.forEach(id => queryParams.append("tags[]", id));
    selectedFilters.categories.forEach(id => queryParams.append("categories[]", id));
    if (debouncedNameSearch) {queryParams.append("name", debouncedNameSearch);}

    // SOLO al primo caricamento, aggiungi i parametri da location.search
    if (isInitialLoad) {
      const urlParams = parseQueryParams(location.search);
      Object.entries(urlParams).forEach(([key, value]) => {
        if (!queryParams.has(key)) queryParams.append(key, value);
      });
    }

    const url = `${process.env.REACT_APP_API_URL}/innovations?${queryParams.toString()}`;
    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` },
    });

    const data = response.data;
    setStartupsData(data.data);
    setTotalResults(data.total);
    setTotalPages(parseInt(data.last_page, 10));
  } catch (err) {
    setError(err.message || "Errore nel caricamento dei dati");
  } finally {
    setLoading(false);
    if (isInitialLoad) setIsInitialLoad(false); // ✅ Disattiva dopo il primo uso
  }
};

  // Funzione per resettare i filtri
  const resetFilters = () => {
    clearFiltersFromSession();

    const defaultFilters = {
      chat: "",
      business_models: [],
      stages: [],
      company_types: [],
      target_markets: [],
      tags: [],
      trade_shows: [],
      order_by: "",
      order_dir: "",
      countries: [],
      year_min: null,
      year_max: null,
      categories: []
    };

    setSelectedFilters(defaultFilters);
    setOrderDir("desc");
    setPage(1);
    setResetSignal(!resetSignal);
    setDialogPlaceholder("Sectors");
    setClientName("");
    setNameSearch("");

    navigate("/startups"); // pulizia URL
    setComponentKey((prevKey) => prevKey + 1);
  };


  // useEffect per inizializzare i filtri da URL
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const hasQueryParams = urlParams.toString().length > 0;
    
    if (hasQueryParams) {
      // ✅ 1. Pulizia sessionStorage se ci sono query string
      clearFiltersFromSession();
    
      // ✅ 2. Applica i filtri dall’URL
      const filtersFromURL = {
        categories: urlParams.getAll("categories[]"),
        tags: urlParams.getAll("tags[]"),
        trade_shows: urlParams.getAll("trade_shows[]"),
        countries: urlParams.getAll("country"),
        business_models: urlParams.getAll("business_model"),
        company_types: urlParams.getAll("type"),
        stages: urlParams.getAll("status"),
        target_markets: urlParams.getAll("target_markets[]"),
        chat: urlParams.get("chat") || "",
        year_min: urlParams.get("year_min") ? parseInt(urlParams.get("year_min"), 10) : null,
        year_max: urlParams.get("year_max") ? parseInt(urlParams.get("year_max"), 10) : null,
        order_by: "",
        order_dir: ""
      };
    
      setSelectedFilters((prev) => ({ ...prev, ...filtersFromURL }));
    
      const name = urlParams.get("name");
      if (name) setNameSearch(name);
    
      const namePlaceholder = urlParams.getAll("name[]");
      setDialogPlaceholder(namePlaceholder.length > 0 ? namePlaceholder[0] : "Sectors");
    
      const client = urlParams.get("client");
      if (client) setClientName(client);
    } else {
      // ✅ 3. Nessun parametro in URL → usa sessionStorage se presente
      const saved = getFiltersFromSession();
      if (saved) {
        setSelectedFilters(saved.filters || {});
        setNameSearch(saved.nameSearch || "");
        if (saved.dialogPlaceholder) {
          setDialogPlaceholder(saved.dialogPlaceholder); // <--- QUESTA È LA RIGA CHIAVE
        }
      }
    }
  }, []);



  useEffect(() => {
    saveFiltersToSession(selectedFilters, nameSearch, dialogPlaceholder);
  }, [selectedFilters, nameSearch, dialogPlaceholder]);

  useEffect(() => {
    if (
      selectedFilters.year_min !== null &&
      selectedFilters.year_max !== null
    ) {
      setTempYearRange([selectedFilters.year_min, selectedFilters.year_max]);
    } else if (filters.year_min && filters.year_max) {
      setTempYearRange([filters.year_min, filters.year_max]);
    }
  }, [filters, selectedFilters.year_min, selectedFilters.year_max]);

  // useEffect per chiamare fetchData
  useEffect(() => {
    fetchData(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, selectedFilters, orderDir, location.search, debouncedNameSearch]);

  // useEffect per caricare i filtri una sola volta
  useEffect(() => {
    fetchFilterData();
  }, []);

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleFilterChange = (filterName, selectedValues) => {
    setSelectedFilters((prev) => ({
      ...prev,
      [filterName]: selectedValues
    }));
  };

  const handleOrderChange = (event) => {
    const selectedValue = event.target.value;
    if (selectedValue === "reset") {
      resetFilters();
    } else {
      setOrderDir(selectedValue);
    }
  };

  // Selezione categoria singola
  const handleCategorySelect = (categoryId) => {
    // Se categoryId è una stringa, va bene. 
    // Se fosse un numero, potresti fare: categoryId.toString()
    setSelectedFilters((prev) => ({
      ...prev,
      categories: [categoryId]
    }));
  };

  const updateURLWithFilters = (filters, name) => {
    const queryParams = new URLSearchParams();

    if (name) queryParams.set("name", name);
    if (filters.chat) queryParams.set("chat", filters.chat);
    filters.countries.forEach((id) => queryParams.append("country", id));
    filters.business_models.forEach((id) => queryParams.append("business_model", id));
    filters.company_types.forEach((id) => queryParams.append("type", id));
    filters.stages.forEach((id) => queryParams.append("status", id));
    filters.target_markets.forEach((id) => queryParams.append("target_markets[]", id));
    filters.tags.forEach((id) => queryParams.append("tags[]", id));
    filters.trade_shows.forEach((id) => queryParams.append("tags[]", id));
    filters.categories.forEach((id) => queryParams.append("categories[]", id));
    if (filters.year_min) queryParams.set("year_min", filters.year_min);
    if (filters.year_max) queryParams.set("year_max", filters.year_max);

    navigate(`/startups?${queryParams.toString()}`, { replace: true });
  };

  useEffect(() => {
    updateURLWithFilters(selectedFilters, nameSearch);
  }, [selectedFilters, nameSearch]);



  const listStartups = startupsData.map((s) => (
    <StartupsCard
      key={s.id}
      id={s.id}
      name={s.name}
      year={s.founding_year}
      description={
        s.description
          ? s.description.replace(/<\/?[^>]+(>|$)/g, "")
          : "No description available"
      }
      logo={s.logo_url || `${process.env.REACT_APP_BASE_URL}/default-logo.png`}
      countryCode={s.country?.iso || "US"}
      tags={
        s.taxonomies?.tags?.length > 0
          ? s.taxonomies.tags.map((tag, index) => ({
              id: index,
              label: tag.name || "N/A",
              variant: "outlined"
            }))
          : [{ id: 0, label: "No tags", variant: "outlined" }]
      }
      link={`/startups/startup/${s.id}`}
      isBookmarked={s.is_bookmarked}
    />
  ));

  return (
    <div
      key={componentKey}
      className="bg-black p-5 min-h-[100vh] flex flex-col gap-10"
    >

      {/* Filtri e header */}
      <div className="flex flex-col gap-2">
        <h1 className="text-6xl font-medium">Startups</h1>

        {/* Qui mostri il client se presente */}
        <p>
          {loading
            ? "Loading results..."
            : `${clientName ? clientName + " - " : ""}Showing ${totalResults} results`
          }
        </p>
        

        <div className="flex flex-wrap gap-4">

          <FormControl sx={{ minWidth: 200 }}>
            <input
              id="name-search"
              type="text"
              value={nameSearch}
              onChange={(e) => setNameSearch(e.target.value)}
              placeholder="Search by name..."
              style={{
                padding: "10px",
                fontFamily: "Urbanist, sans-serif",
                backgroundColor: "black",
                color: "white",
                border: "1px solid white",
                borderRadius: "4px",
                flexGrow: "1"
              }}
              className="custom-input"
            />
          </FormControl>
          
          <SectorFilterDialog
            onCategorySelect={handleCategorySelect}
            resetSignal={resetSignal}
            placeholder={dialogPlaceholder}
          />

          <FormControl sx={{ px: 0 }}>
            <YearRangeFilter
              placeholder="Years"
              apiUrl={`${process.env.REACT_APP_API_URL}/values/innovation-filters`}
              onChange={([year_min, year_max]) => {
                setSelectedFilters((prev) => ({
                  ...prev,
                  year_min,
                  year_max,
                }));
              }}
            />
          </FormControl>

          <FilterSelect
            key={`countries-${resetSignal}`}
            label="Location"
            options={[{ id: "", name: "Reset" }, ...(filters.countries || [])]}
            selected={selectedFilters.countries}
            onChange={(selected) => handleFilterChange("countries", selected)}
          />
                      
          <FilterSelect
            key={`company-types-${resetSignal}`}
            label="Type"
            options={[{ id: "", name: "Reset" }, ...(filters.company_types || [])]}
            selected={selectedFilters.company_types}
            onChange={(selected) => handleFilterChange("company_types", selected)}
          />
                      
          <FilterSelect
            key={`target-markets-${resetSignal}`}
            label="Target Markets"
            options={[{ id: "", name: "Reset" }, ...(filters.target_markets || [])]}
            selected={selectedFilters.target_markets}
            onChange={(selected) => handleFilterChange("target_markets", selected)}
            width={"170px"}
          />
                      
          <FilterSelect
            key={`business-models-${resetSignal}`}
            label="Business Models"
            options={[{ id: "", name: "Reset" }, ...(filters.business_models || [])]}
            selected={selectedFilters.business_models}
            onChange={(selected) => handleFilterChange("business_models", selected)}
            width={"170px"}
          />
                      
          <FilterSelect
            key={`stages-${resetSignal}`}
            label="Status"
            options={[{ id: "", name: "Reset" }, ...(filters.stages || [])]}
            selected={selectedFilters.stages}
            onChange={(selected) => handleFilterChange("stages", selected)}
          />
                      
          <FilterSelect
            key={`tags-${resetSignal}`}
            label="Tags"
            options={[{ id: "", name: "Reset" }, ...(filters.tags || [])]}
            selected={selectedFilters.tags}
            onChange={(selected) => handleFilterChange("tags", selected)}
          />
                      
          <FilterSelect
            key={`trade-shows-${resetSignal}`}
            label="Trade Shows"
            options={[{ id: "", name: "Reset" }, ...(filters.trade_shows || [])]}
            selected={selectedFilters.trade_shows}
            onChange={(selected) => handleFilterChange("trade_shows", selected)}
            width={"140px"}
          />


          <FormControl sx={{ minWidth: 120 }}>
            <Select value={orderDir} onChange={handleOrderChange}>
              <MenuItem value="asc">Older</MenuItem>
              <MenuItem value="desc">Recent</MenuItem>
              <MenuItem value="reset">Reset</MenuItem>
            </Select>
          </FormControl>                  

          <Button variant="outlined" onClick={resetFilters} sx={{ ...buttonStyles }}>
            Reset All Filters
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-4">
        <FormControlLabel
          control={
            <Checkbox
              checked={selectedFilters.chat === "true"}
              onChange={(event) =>
                handleFilterChange("chat", event.target.checked ? "true" : "")
              }
              sx={{
                color: "#C5FF55",
                "&.Mui-checked": { color: "#C5FF55" },
                "& .MuiSvgIcon-root": { fontFamily: "Urbanist, sans-serif" },
                "& .MuiCheckbox-root": { border: "1px solid #C5FF55" }
              }}
            />
          }
          label="Show Startups Available to chat only"
        />
      </div>

      <div
        className="grid gap-3"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(1, 1fr)",
          gridGap: "15px",
          alignItems: "stretch"
        }}
      >
        <style>
          {`
            @media (min-width: 768px) {
              div.grid {
                grid-template-columns: repeat(2, 1fr) !important;
              }
              div.grid > div {
                display: flex !important;
                flex-direction: column;
              }
            }
            .MuiFormLabel-root {
              background-color: #000 !important;
            }

            @media (min-width: 1300px) {
              div.grid {
                grid-template-columns: repeat(3, 1fr) !important;
              }
              div.grid > div {
                display: flex !important;
                flex-direction: column;
              }
            }
            .MuiFormLabel-root {
              background-color: #000 !important;
            }
          `}
        </style>
        {loading ? (
          [...Array(9)].map((_, index) => <SkeletonCard key={index} />)
        ) : error ? (
          <p style={{ color: "red" }}>{error}</p>
        ) : startupsData.length > 0 ? (
          listStartups
        ) : (
          <p>No startups found</p>
        )}
      </div>

      <div className="flex justify-center mt-6">
        <Pagination
          count={totalPages}
          page={page}
          onChange={handlePageChange}
          color="primary"
          className="pagination"
        />
      </div>
    </div>
  );
}

/**
 * FilterSelect
 * Componente generico per creare menu di selezione multipla.
 */
const FilterSelect = ({
  label,
  options,
  selected,
  onChange,
  multiple = true,
  width
}) => {
  return (
    <FormControl sx={{ minWidth: 120, width: width || "auto" }}>
      <InputLabel sx={{ fontFamily: "Urbanist, sans-serif" }}>{label}</InputLabel>
      <Select
        multiple={multiple}
        value={selected || (multiple ? [] : "")}
        onChange={(e) => {
          const value = e.target.value;

          if (Array.isArray(value) && value.includes("")) {
            onChange([]);
          } else if (value === "") {
            onChange([]);
          } else {
            onChange(value);
          }
        }}
        sx={{
          border:
            (Array.isArray(selected) && selected.length > 0) ||
            (typeof selected === "string" && selected !== "") ||
            (typeof selected === "number" && selected !== 0)
              ? "1px solid rgb(var(--global-color-primary))"
              : "1px solid white",
          ".MuiOutlinedInput-notchedOutline": {
            border: "0"
          },
          ".MuiFormLabel-root .MuiInputLabel-root": {
            backgroundColor: "#000 !important"
          },
          ".Mui-focused": {
            border: "0 !important"
          }
        }}
        renderValue={(selected) => {
          if (multiple) {
            return selected
              .map((sel) => options.find((option) => option.id === sel)?.name || "")
              .join(", ");
          } else {
            return options.find((option) => option.id === selected)?.name || "";
          }
        }}
      >
        {options.map((option) => (
          <MenuItem key={option.id} value={option.id}>
            {multiple && (
              <Checkbox
                checked={selected.includes(option.id)}
                sx={{
                  color: "#C5FF55",
                  "&.Mui-checked": { color: "#C5FF55" },
                }}
              />
            )}
            <ListItemText primary={option.name} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};


const buttonStyles = {
  fontFamily: "Urbanist, sans-serif",
  border: "1px solid rgb(255, 255, 255, .3)",
  color: "rgb(255, 255, 255, .3)"
};

export default Startups;
